/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Typography,
  TextField,
  MenuItem,
  Grid,
  Autocomplete,
  Checkbox,
} from '@mui/material';
import {
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckBox as CheckBoxIcon,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { apiUrl } from '../../utilities/apiUrl';
import {
  GeneralModal,
  ModalButton,
  CustomTextField,
  SelectDropdown,
} from '../index';

const checkBoxOutlineIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkBoxIcon = <CheckBoxIcon fontSize="small" />;

const emptyForm = {
  title: '',
  message: '',
  targettype: 'ALL',
  targetempids: [],
  trigger: '',
  repeat: 'ONCE',
  active: true,
  notes: '',
  startdate: null,
  enddate: null,
  linkLabel1: '',
  linkUrl1: '',
  linkLabel2: '',
  linkUrl2: '',
  linkLabel3: '',
  linkUrl3: '',
};

export const ReminderModal = (props) => {
  const reminder = props.reminder;
  const employeeList = props.employeeList || [];
  const isEditing = !!reminder;
  const [form, setForm] = useState(emptyForm);

  const employeeOptions = employeeList.map((emp) => ({
    id: emp._id,
    name: `${emp.firstname} ${emp.lastname}`,
  }));

  // POPULATE FORM WHEN OPENING (EDIT: FROM EXISTING REMINDER, CREATE: BLANK)
  useEffect(() => {
    if (!props.open) return;

    if (reminder) {
      setForm({
        title: reminder.title || '',
        message: reminder.message || '',
        targettype: reminder.targettype || 'ALL',
        targetempids: reminder.targetempids || [],
        trigger: reminder.trigger || '',
        repeat: reminder.repeat || 'ONCE',
        active: reminder.active ?? true,
        notes: reminder.notes || '',
        startdate: reminder.startdate ? new Date(reminder.startdate) : null,
        enddate: reminder.enddate ? new Date(reminder.enddate) : null,
        linkLabel1: reminder.links?.[0]?.label || '',
        linkUrl1: reminder.links?.[0]?.url || '',
        linkLabel2: reminder.links?.[1]?.label || '',
        linkUrl2: reminder.links?.[1]?.url || '',
        linkLabel3: reminder.links?.[2]?.label || '',
        linkUrl3: reminder.links?.[2]?.url || '',
      });
    } else {
      setForm(emptyForm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, reminder]);

  const setField = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const isValid =
    form.title.trim() !== '' &&
    form.message.trim() !== '' &&
    form.trigger !== '' &&
    (form.targettype !== 'SPECIFIC' || form.targetempids.length > 0);

  const handleSave = () => {
    const targetempids = employeeOptions
      .filter((opt) => form.targetempids.includes(opt.id))
      .map((opt) => opt.id);

    const links = [
      { label: form.linkLabel1, url: form.linkUrl1 },
      { label: form.linkLabel2, url: form.linkUrl2 },
      { label: form.linkLabel3, url: form.linkUrl3 },
    ].filter((link) => link.label.trim() !== '' && link.url.trim() !== '');

    const payload = {
      title: form.title,
      message: form.message,
      links,
      targettype: form.targettype,
      targetempids: form.targettype === 'SPECIFIC' ? targetempids : [],
      trigger: form.trigger,
      repeat: form.repeat,
      active: form.active,
      notes: form.notes,
      startdate: form.startdate,
      enddate: form.enddate,
    };

    fetch(apiUrl(isEditing ? `/reminder/${reminder._id}` : '/reminder'), {
      method: isEditing ? 'PUT' : 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          props.onSaved(isEditing ? 'Reminder updated.' : 'Reminder created.');
        } else {
          alert(data.message);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  return (
    <GeneralModal
      open={props.open}
      onClose={props.onClose}
      style={{ width: '60%', maxHeight: '85vh', overflowY: 'auto' }}
    >
      <Typography align="center" sx={{ mb: 1, fontWeight: 600 }} variant="h5">
        {isEditing ? 'Edit Reminder' : 'Add Reminder'}
      </Typography>
      <Grid container spacing={4}>
        <Grid item xs={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Content
          </Typography>
          <CustomTextField
            required
            label="Title"
            value={form.title}
            onChange={setField('title')}
          />
          <CustomTextField
            required
            multiline
            maxRows={8}
            label="Message"
            value={form.message}
            onChange={setField('message')}
          />
          <CustomTextField
            label="Link Label 1"
            value={form.linkLabel1}
            onChange={setField('linkLabel1')}
          />
          <CustomTextField
            label="Link URL 1"
            value={form.linkUrl1}
            onChange={setField('linkUrl1')}
          />
          <CustomTextField
            label="Link Label 2"
            value={form.linkLabel2}
            onChange={setField('linkLabel2')}
          />
          <CustomTextField
            label="Link URL 2"
            value={form.linkUrl2}
            onChange={setField('linkUrl2')}
          />
        </Grid>
        <Grid item xs={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Settings
          </Typography>
          <SelectDropdown
            fullWidth
            margin="normal"
            label="Status"
            value={form.active}
            onChange={setField('active')}
          >
            <MenuItem value={true}>Active</MenuItem>
            <MenuItem value={false}>Inactive</MenuItem>
          </SelectDropdown>
          <SelectDropdown
            fullWidth
            required
            margin="normal"
            label="Show to"
            value={form.targettype}
            onChange={setField('targettype')}
          >
            <MenuItem value="ALL">All Employees</MenuItem>
            <MenuItem value="SPECIFIC">Specific Employees</MenuItem>
          </SelectDropdown>
          {form.targettype === 'SPECIFIC' && (
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={employeeOptions}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={employeeOptions.filter((opt) =>
                form.targetempids.includes(opt.id)
              )}
              onChange={(e, newValue) =>
                setForm((prev) => ({
                  ...prev,
                  targetempids: newValue.map((opt) => opt.id),
                }))
              }
              renderOption={(liProps, option, { selected }) => (
                <li {...liProps} key={option.id}>
                  <Checkbox
                    icon={checkBoxOutlineIcon}
                    checkedIcon={checkBoxIcon}
                    checked={selected}
                    sx={{ mr: 1 }}
                  />
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required={form.targetempids.length === 0}
                  label="Recipients"
                  placeholder="Search employees"
                  margin="normal"
                />
              )}
            />
          )}
          <SelectDropdown
            fullWidth
            required
            margin="normal"
            label="When to show"
            value={form.trigger}
            onChange={setField('trigger')}
          >
            <MenuItem value="AFTER_CHECKIN">After Check-in</MenuItem>
            <MenuItem value="BEFORE_CHECKOUT">Before Check-out</MenuItem>
            <MenuItem value="AFTER_CHECKOUT">After Check-out</MenuItem>
          </SelectDropdown>
          <SelectDropdown
            fullWidth
            required
            margin="normal"
            label="Repeat"
            value={form.repeat}
            onChange={setField('repeat')}
          >
            <MenuItem value="ONCE">Show once</MenuItem>
            <MenuItem value="DAILY">Show every day until turned off</MenuItem>
          </SelectDropdown>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start Date (optional)"
              value={form.startdate}
              onChange={(newValue) =>
                setForm((prev) => ({ ...prev, startdate: newValue }))
              }
              maxDate={form.enddate || undefined}
              renderInput={(params) => (
                <TextField margin="normal" fullWidth {...params} />
              )}
            />
            <DatePicker
              label="End Date (optional)"
              value={form.enddate}
              onChange={(newValue) =>
                setForm((prev) => ({ ...prev, enddate: newValue }))
              }
              minDate={form.startdate || undefined}
              renderInput={(params) => (
                <TextField margin="normal" fullWidth {...params} />
              )}
            />
          </LocalizationProvider>
          <CustomTextField
            multiline
            maxRows={4.5}
            label="Notes (Admin only)"
            value={form.notes}
            onChange={setField('notes')}
          />
        </Grid>
      </Grid>
      <ModalButton
        disable={!isValid}
        primaryBtnName="Save"
        secondaryBtnName="Cancel"
        secondaryClickHandler={props.onClose}
        primaryClickHandler={handleSave}
        style={{ mt: 3 }}
      />
    </GeneralModal>
  );
};
